import { Injectable } from '@angular/core';
import { AuthProvider, CompanyType, CookieAuthService, CookieName } from '@itero/auth-angular';

export interface AuthCookie {
	identityProvider: string | AuthProvider;
	idToken: string;
	accessToken: string;
	sessionId: string;
}

export interface BusinessCookie {
	companyId: number;
	companyType: string | CompanyType;
	impersonatedContactId?: number;
}

export interface InfraCookie {
	region: string;
}

export interface AllInitializationCookie {
	authCookie: AuthCookie;
	businessCookie: BusinessCookie;
	infraCookie: InfraCookie;
	missing: CookieName[];
}

export interface CookieWithMetadata<T> {
	cookie: T;
	missing: CookieName[];
}

@Injectable({
	providedIn: 'root',
})
export class CookieReadWriteService {
	constructor(protected cookieAuthService: CookieAuthService) {}

	get authCookie(): CookieWithMetadata<AuthCookie> {
		const missing: CookieName[] = [];
		const identityProvider = this.getCookie(CookieName.IDENTITYPROVIDER);
		const idToken = this.getCookie(CookieName.IDTOKEN);
		const accessToken = this.getCookie(CookieName.ITEROTOKEN);
		const sessionId = this.getCookie(CookieName.SESSIONID);

		if (!identityProvider) {
			missing.push(CookieName.IDENTITYPROVIDER);
		}
		if (!idToken) {
			missing.push(CookieName.IDTOKEN);
		}
		if (!accessToken) {
			missing.push(CookieName.ITEROTOKEN);
		}
		if (!sessionId) {
			missing.push(CookieName.SESSIONID);
		}

		return {
			cookie: {
				identityProvider: this.toStringOrNull(identityProvider),
				idToken: this.toStringOrNull(idToken),
				accessToken: this.toStringOrNull(accessToken),
				sessionId: this.toStringOrNull(sessionId),
			},
			missing,
		};
	}

	get businessCookie(): CookieWithMetadata<BusinessCookie> {
		const missing: CookieName[] = [];
		const companyId = Number.parseInt(String(this.getCookie(CookieName.COMPANYID)), 10);
		const companyType = this.getCookie(CookieName.COMPANYTYPES);
		const impersonatedContactId = Number.parseInt(String(this.getCookie(CookieName.IMPERSONATEDCONTACTID)), 10);

		if (Number.isNaN(companyId) || companyId <= 0) {
			missing.push(CookieName.COMPANYID);
		}

		if (!companyType) {
			missing.push(CookieName.COMPANYTYPES);
		}

		return {
			cookie: {
				companyId: Number.isNaN(companyId) || companyId <= 0 ? null : companyId,
				companyType: this.toStringOrNull(companyType),
				impersonatedContactId: Number.isNaN(impersonatedContactId) || impersonatedContactId <= 0 ? null : impersonatedContactId,
			},
			missing,
		};
	}

	get infraCookie(): CookieWithMetadata<InfraCookie> {
		const missing: CookieName[] = [];
		const region = this.getCookie(CookieName.REGION);

		if (!region) {
			missing.push(CookieName.REGION);
		}

		return {
			cookie: {
				region: this.toStringOrNull(region),
			},
			missing,
		};
	}

	get allInitializationCookie(): AllInitializationCookie {
		const authCookie = this.authCookie;
		const businessCookie = this.businessCookie;
		const infraCookie = this.infraCookie;
		const missing: CookieName[] = [...authCookie.missing, ...businessCookie.missing, ...infraCookie.missing];

		return {
			authCookie: authCookie.cookie,
			businessCookie: businessCookie.cookie,
			infraCookie: infraCookie.cookie,
			missing,
		};
	}

	writeAuthCookie({ identityProvider, idToken, accessToken, sessionId }: Partial<AuthCookie>) {
		if (identityProvider) {
			this.setCookie(CookieName.IDENTITYPROVIDER, identityProvider);
		}
		if (idToken) {
			this.setCookie(CookieName.IDTOKEN, idToken);
		}
		if (accessToken) {
			this.setCookie(CookieName.ITEROTOKEN, accessToken);
		}
		if (sessionId) {
			this.setCookie(CookieName.SESSIONID, sessionId);
		}
	}

	writeBusinessCookie({ companyId, companyType, impersonatedContactId }: BusinessCookie) {
		this.setCookie(CookieName.COMPANYID, companyId);
		this.setCookie(CookieName.COMPANYTYPES, companyType);
		this.setCookie(CookieName.IMPERSONATEDCONTACTID, impersonatedContactId);
	}

	writeInfraCookie({ region }: InfraCookie) {
		this.setCookie(CookieName.REGION, region);
	}

	clearAuthCookie() {
		this.removeCookie(CookieName.IDENTITYPROVIDER);
		this.removeCookie(CookieName.SESSIONID);
		this.removeCookie(CookieName.ITEROTOKEN);
		this.removeCookie(CookieName.IDTOKEN);
	}

	clearBusinessCookie() {
		this.removeCookie(CookieName.COMPANYID);
		this.removeCookie(CookieName.COMPANYTYPES);
		this.removeCookie(CookieName.IMPERSONATEDCONTACTID);
	}

	clearInfraCookie() {
		this.removeCookie(CookieName.REGION);
	}

	private get defaultOptions() {
		return { path: '/' };
	}

	private getCookie(name: CookieName): ReturnType<typeof this.cookieAuthService.getCookie> {
		return this.cookieAuthService.getCookie(name);
	}

	private setCookie(...args: Parameters<typeof this.cookieAuthService.setCookie>) {
		const [name, value, options = this.defaultOptions] = args;

		this.cookieAuthService.setCookie(name, value, options);
	}

	private removeCookie(...args: Parameters<typeof this.cookieAuthService.removeCookie>) {
		const [name, options = this.defaultOptions] = args;

		this.cookieAuthService.removeCookie(name, options);
	}

	private toStringOrNull(value: ReturnType<typeof this.cookieAuthService.getCookie>): string | null {
		return value === null || value === undefined ? null : String(value);
	}
}
